import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import {
  DOCUMENT_COLLAB_HUB_PATH,
  type DocumentEditLockHolder,
  type DocumentFileSavedNotice,
  type DocumentRosterParticipant
} from './documentCollaborationTypes';

const HEARTBEAT_MS = 4000;
const TYPING_NOTIFY_MS = 300;

export interface DocumentCollaborationClientOptions {
  mountId: string;
  path: string;
  onRosterChanged: (roster: DocumentRosterParticipant[]) => void;
  onFileSaved: (notice: DocumentFileSavedNotice) => void;
  onEditLockChanged: (holder: DocumentEditLockHolder | null) => void;
  onEditLockDenied: (holder: DocumentEditLockHolder | null) => void;
  onConnectedChange?: (connected: boolean) => void;
}

export interface DocumentCollaborationClient {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  /** SignalR connection id for this tab (stable until reconnect). */
  getConnectionId: () => string | null;
  /** Returns false when the exclusive edit lock was denied. */
  notifyEditing: (isEditing: boolean) => Promise<boolean>;
  notifyTyping: () => void;
}

function normalizeRosterParticipant(raw: Record<string, unknown>): DocumentRosterParticipant {
  return {
    userId: String(raw.userId ?? raw.UserId ?? ''),
    displayName: String(raw.displayName ?? raw.DisplayName ?? ''),
    upn: (raw.upn ?? raw.Upn ?? null) as string | null,
    hasPhoto: (raw.hasPhoto ?? raw.HasPhoto ?? null) as boolean | null,
    connections: Number(raw.connections ?? raw.Connections ?? 0),
    isEditing: Boolean(raw.isEditing ?? raw.IsEditing),
    isTyping: Boolean(raw.isTyping ?? raw.IsTyping),
    lastTypingActivityUtc: (raw.lastTypingActivityUtc ?? raw.LastTypingActivityUtc ?? null) as
      | string
      | null
  };
}

function normalizeRoster(raw: unknown): DocumentRosterParticipant[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) =>
    normalizeRosterParticipant(typeof row === 'object' && row != null ? (row as Record<string, unknown>) : {})
  );
}

function normalizeEditLockHolder(raw: unknown): DocumentEditLockHolder | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const userId = String(row.userId ?? row.UserId ?? '').trim();
  if (!userId) return null;
  const acquired =
    row.acquiredAtUtc ?? row.AcquiredAtUtc ?? row.acquiredAt ?? row.AcquiredAt ?? null;
  return {
    userId,
    displayName: String(row.displayName ?? row.DisplayName ?? ''),
    connectionId: String(row.connectionId ?? row.ConnectionId ?? ''),
    acquiredAtUtc: acquired != null ? String(acquired) : new Date().toISOString()
  };
}

/**
 * Thin wrapper over a SignalR connection to the document collaboration hub. Joins one file,
 * heartbeats presence, surfaces roster + file-saved + edit-lock pushes, and re-joins after reconnects.
 */
export function createDocumentCollaborationClient(
  options: DocumentCollaborationClientOptions
): DocumentCollaborationClient {
  const {
    mountId,
    path,
    onRosterChanged,
    onFileSaved,
    onEditLockChanged,
    onEditLockDenied,
    onConnectedChange
  } = options;

  let connection: HubConnection | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let disposed = false;
  let lastTypingNotifyAt = 0;

  const leaveNow = () => {
    const conn = connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    conn.invoke('LeaveAsync').catch(() => undefined);
  };

  const onPageHide = () => leaveNow();

  const stopHeartbeat = () => {
    if (heartbeatTimer != null) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const startHeartbeat = (conn: HubConnection) => {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (conn.state === HubConnectionState.Connected) {
        conn.invoke('HeartbeatAsync').catch(() => {
          /* transient; next tick retries */
        });
      }
    }, HEARTBEAT_MS);
  };

  const join = async (conn: HubConnection) => {
    try {
      const roster = await conn.invoke<unknown>('JoinAsync', mountId, path);
      onRosterChanged(normalizeRoster(roster));
    } catch {
      /* roster will arrive via RosterChanged once the server processes the join */
    }
  };

  const notifyEditing = async (isEditing: boolean): Promise<boolean> => {
    const conn = connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return false;
    try {
      const result = await conn.invoke<{
        accepted?: boolean;
        Accepted?: boolean;
        holder?: unknown;
        Holder?: unknown;
      }>('NotifyEditingAsync', isEditing);
      const accepted = Boolean(result?.accepted ?? result?.Accepted);
      const holder = normalizeEditLockHolder(result?.holder ?? result?.Holder ?? null);
      if (!accepted) {
        if (isEditing) onEditLockDenied(holder);
        onEditLockChanged(holder);
        return false;
      }
      // Apply lock state from the invoke result immediately so the shell does not wait on the
      // group broadcast (and cannot mis-classify this tab as a peer while identity is still loading).
      onEditLockChanged(holder);
      return true;
    } catch {
      return false;
    }
  };

  const notifyTyping = () => {
    const now = Date.now();
    if (now - lastTypingNotifyAt < TYPING_NOTIFY_MS) return;
    lastTypingNotifyAt = now;
    const conn = connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    conn.invoke('NotifyTypingAsync').catch(() => {
      /* transient */
    });
  };

  const start = async () => {
    if (connection || disposed) return;

    const conn = new HubConnectionBuilder()
      .withUrl(DOCUMENT_COLLAB_HUB_PATH, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    conn.on('RosterChanged', (roster: unknown) => {
      onRosterChanged(normalizeRoster(roster));
    });
    conn.on('FileSaved', (notice: DocumentFileSavedNotice) => {
      if (notice) onFileSaved(notice);
    });
    conn.on('EditLockChanged', (holder: unknown) => {
      onEditLockChanged(normalizeEditLockHolder(holder));
    });
    conn.on('EditLockDenied', (holder: unknown) => {
      onEditLockDenied(normalizeEditLockHolder(holder));
    });

    conn.onreconnected(() => {
      onConnectedChange?.(true);
      void join(conn);
    });
    conn.onreconnecting(() => onConnectedChange?.(false));
    conn.onclose(() => {
      onConnectedChange?.(false);
      stopHeartbeat();
    });

    window.addEventListener('pagehide', onPageHide);
    connection = conn;
    try {
      await conn.start();
      if (disposed) {
        await conn.stop();
        return;
      }
      onConnectedChange?.(true);
      await join(conn);
      startHeartbeat(conn);
    } catch {
      onConnectedChange?.(false);
    }
  };

  const stop = async () => {
    disposed = true;
    window.removeEventListener('pagehide', onPageHide);
    stopHeartbeat();
    const conn = connection;
    connection = null;
    if (!conn) return;
    try {
      if (conn.state === HubConnectionState.Connected) {
        await conn.invoke('LeaveAsync').catch(() => undefined);
      }
    } finally {
      await conn.stop().catch(() => undefined);
    }
  };

  const getConnectionId = () => {
    const conn = connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return null;
    return conn.connectionId ?? null;
  };

  return { start, stop, getConnectionId, notifyEditing, notifyTyping };
}
